/* eslint-disable no-unused-vars */
'use client';

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Footer from '../Footer/Footer';
import { getUser } from "../Utility/getUser"; // Import the getUser function

export default function Cart({ fetchCartCount }) {
  const [products, setProducts] = useState([]);
  const [isRemoving, setIsRemoving] = useState(false); // State to track if a remove operation is in progress
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const user = getUser(); // Get the user information from local storage
        const response = await fetch(`https://panel.masit.net/api/view.php?table=cartview&cart_usersid=${user.users_id}`); // Replace with your API endpoint
        const data = await response.json();
        if (Array.isArray(data)) {
          setProducts(data);
        } else {
          fetchCartCount(); // Update the cart count in the Navbar  

    
        }
      } catch (error) {
        console.error('Error fetching cart data:', error);
      }
    };

    fetchCart();
  }, [fetchCartCount]);

  const handleRemove = async (id) => {
    setIsRemoving(true); // Disable the remove button
    try {
      const user = getUser(); // Get the user information from local storage

      if (!user) {
        setError('User not logged in');
        alert('User not logged in');
        return;
      }

      const response = await fetch('https://panel.masit.net/api/product/delete.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          table: 'cart',
          cart_id: id,
          cart_usersid: user.users_id, // Use the user ID from the getUser function
        }),
      });

      const data = await response.json();
      console.log(data.message);
      setProducts((prevProducts) => prevProducts.filter((product) => product.cart_id !== id));
      fetchCartCount(); // Update the cart count in the Navbar  
    } catch (error) {
      console.error('Error removing item from cart:', error);
    } finally {
      setIsRemoving(false); // Re-enable the remove button
    }
  };

  const handleQuantityChange = async (id, quantity) => {
    const product = products.find((product) => product.cart_id === id);
    const newQuantity = Number(quantity);

    if (newQuantity > product.countitems) {
      // Add item to cart
      try {
        const user = getUser(); // Get the user information from local storage

        if (!user) {
          setError('User not logged in');
          alert('User not logged in');
          return;
        }

        const response = await fetch('https://panel.masit.net/api/product/create.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            table: 'cart',
            data: {
              cart_usersid: user.users_id, // Use the user ID from the getUser function
              cart_itemsid: product.items_id.toString(),
            },
          }),
        });
        const data = await response.json();
        console.log(data.message);
        fetchCartCount(); // Update the cart count in the Navbar
      } catch (error) {
        console.error('Error adding item to cart:', error);
      }
    } else if (newQuantity < product.countitems) {
      // Remove item from cart
      setIsRemoving(true); // Disable the remove button
      try {
        const user = getUser(); // Get the user information from local storage

        if (!user) {
          setError('User not logged in');
          alert('User not logged in');
          return;
        }

        const del_response = await fetch('https://panel.masit.net/api/product/delete.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            table: 'cart',
            cart_id: id,
            cart_usersid: user.users_id, // Use the user ID from the getUser function
          }),
        });
        const del_data = await del_response.json();
        console.log(del_data.message);
        fetchCartCount(); // Update the cart count in the Navbar  
      } catch (error) {
        console.error('Error removing item from cart:', error);
      } finally {
        setIsRemoving(false); // Re-enable the remove button
      }
    }

    setProducts(products.map((product) =>
      product.cart_id === id ? { ...product, countitems: newQuantity } : product
    ));
  };

  const totalPrice = products.reduce((total, item) => total + item.items_price * item.countitems, 0);

  const handleCheckout = () => {
    window.location.href = '/checkout';
  };

  return (
    <div className="bg-white dark:bg-gray-900 dark:text-white text-black min-h-screen">
      <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl mb-8">
          Your Cart
        </h1>
        <div className="grid grid-cols-1 gap-6">
          {products.map((item) => (
            <div key={item.cart_id} className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <img src={item.items_image} alt={item.imageAlt} className="w-20 h-20 object-cover rounded-lg" />
                <div className="ml-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">{item.items_name}</h2>
                  <p className="text-gray-500 dark:text-gray-300 pb-1">{item.items_desc}</p>
                  <p className="text-gray-500 dark:text-gray-300 pb-1">€{item.items_price}</p>
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="number"
                  value={item.countitems}
                  min="1" // Set minimum value to 1
                  id='quantity_input'
                  onChange={(e) => handleQuantityChange(item.cart_id, e.target.value)}
                  className="w-16 text-center border text-black border-gray-300 dark:border-gray-700 rounded-md"
                />
                {item.countitems === 1 && (
                  <button id='remove_btn'
                    onClick={() => handleRemove(item.cart_id)}
                    className="ml-4 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                    disabled={isRemoving} // Disable the button while removing
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
            Total: €{totalPrice}
          </h2>
          <button 
            onClick={handleCheckout}
            className="mt-4 w-full bg-red-600 hover:bg-red-500 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Proceed to Checkout
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}

Cart.propTypes = {
  fetchCartCount: PropTypes.func.isRequired,
};